// src/products/questions/pages/SupportQuestionsPage.js
// Dedicated page for General/Support questions - users contact site administrators
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HelpCircle, MessageSquare, Plus, AlertCircle, CheckCircle, TrendingUp, Clock as ClockIcon, CheckCircle as CheckIcon, Mail } from 'lucide-react';
import simpleQuestionsApi from '../services/simpleQuestionsApi';
import ContextAwareQuestionForm from '../components/ContextAwareQuestionForm';
import { useGlobalStore } from '../../../state/globalStore';
import { formatDate } from '../utils';
import { supabase } from '../../../config';

const SupportQuestionsPage = ({ user: userProp, isDealQuestions = false, isSingleDealroom = false }) => {
  const { projectId: routeProjectId } = useParams();
  const { user: globalUser, userProfile, loading: userLoading } = useGlobalStore();
  const user = userProp || globalUser;
  
  // Debug logging
  console.log('SupportQuestionsPage - User state:', { 
    userId: user?.id, 
    isSuperAdmin: userProfile?.is_super_admin,
    userLoading,
    userProp: userProp?.id,
    isDealQuestions
  });
  const [questions, setQuestions] = useState([]);
  const [groupedQuestions, setGroupedQuestions] = useState({});
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    category: 'all'
  });

  const navigate = useNavigate();
  
  // Determine module type based on page type
  const moduleType = isDealQuestions ? 'dataroom' : 'general';
  const selectedProjectId = isSingleDealroom && routeProjectId ? parseInt(routeProjectId, 10) : null;
  const selectedProject = projects.find(p => p.id === selectedProjectId);
  
  const pageTitle = isSingleDealroom && selectedProject 
    ? `${selectedProject.project_name} - Questions` 
    : isDealQuestions ? 'Dealroom Questions' : 'Support Questions';
  const pageSubtitle = isSingleDealroom 
    ? 'All questions for this dealroom' 
    : isDealQuestions ? 'Questions organized by dealroom' : 'Get help from our support team';
  const buttonText = isDealQuestions ? 'Submit Deal Question' : 'Ask Support';

  // Helper function to get project name by ID
  const getProjectName = useCallback((projectId) => {
    const project = projects?.find(p => p.id === projectId);
    return project?.project_name || `Project ${projectId}`;
  }, [projects]);

  const loadProjects = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      // Get projects where user is creator or has access via dataroom_users
      const { data: userProjects, error: userError } = await supabase
        .from('dataroom_users')
        .select('project_id, dataroom_projects!inner(id, project_name)')
        .eq('user_id', user.id);

      if (userError) throw userError;

      // Get projects user created
      const { data: createdProjects, error: createdError } = await supabase
        .from('dataroom_projects')
        .select('id, project_name')
        .eq('created_by', user.id);

      if (createdError) throw createdError;

      // Combine and deduplicate
      const allProjects = [
        ...userProjects.map(up => up.dataroom_projects),
        ...createdProjects
      ];
      
      const uniqueProjects = Array.from(
        new Map(allProjects.map(p => [p.id, p])).values()
      );

      setProjects(uniqueProjects);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  }, [user?.id]);

  // Load dealroom projects (only for deal questions)
  useEffect(() => {
    if (isDealQuestions && user?.id) {
      loadProjects();
    }
  }, [isDealQuestions, user?.id, loadProjects]);

  useEffect(() => {
    // Only load questions when user is available and not loading
    if (user?.id && !userLoading) {
      console.log(`Triggering load for ${moduleType} questions, user:`, user.id);
      loadSupportQuestions();
    } else {
      console.log('Waiting for user to load...', { userId: user?.id, userLoading });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, user?.id, userLoading, moduleType, isDealQuestions]);

  const loadSupportQuestions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`Loading ${moduleType} questions for user:`, user?.id, 'is_super_admin:', userProfile?.is_super_admin);
      
      const result = await simpleQuestionsApi.getQuestions({
        module_type: moduleType,
        status: filters.status !== 'all' ? filters.status : undefined,
        category: filters.category !== 'all' ? filters.category : undefined,
        search: filters.search || undefined,
        current_user_id: user?.id,
        is_super_admin: userProfile?.is_super_admin || false
      });
      
      if (result.success) {
        setQuestions(result.data || []);
      } else {
        setError('Failed to load support questions');
      }
    } catch (err) {
      console.error('Error loading support questions:', err);
      setError('An error occurred while loading questions');
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionCreated = (newQuestion) => {
    setQuestions(prev => [newQuestion, ...prev]);
    setShowQuestionForm(false);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Open' },
      answered: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Answered' },
      closed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Closed' },
      duplicate: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Duplicate' },
      out_of_scope: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Out of Scope' }
    };
    
    const config = statusConfig[status] || statusConfig.open;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const filteredQuestions = questions.filter(q => {
    // Filter by single dealroom if applicable
    if (isSingleDealroom && selectedProjectId && q.project_id !== selectedProjectId) {
      return false;
    }
    // Filter by search
    if (filters.search && !q.title.toLowerCase().includes(filters.search.toLowerCase()) && 
        !q.description?.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    return true;
  });

  // Calculate statistics for each dealroom
  const calculateDealroomStats = useCallback((projectQuestions) => {
    const total = projectQuestions.length;
    const open = projectQuestions.filter(q => q.status === 'open').length;
    const answered = projectQuestions.filter(q => q.status === 'answered' || q.status === 'closed').length;
    const unread = projectQuestions.filter(q => !q.read_by_creator).length; // Assuming we have this field
    
    return { total, open, answered, unread };
  }, []);

  // Group questions by dealroom (only for deal questions and not single dealroom view)
  useEffect(() => {
    if (isDealQuestions && !isSingleDealroom && filteredQuestions.length >= 0) {
      console.log('Grouping deal questions by dealroom...');
      const grouped = {};
      
      // Get all unique project IDs from filtered questions
      const projectIds = [...new Set(filteredQuestions.map(q => q.project_id))];
      
      // Create groups for each project
      projectIds.forEach(pid => {
        const projectQuestions = filteredQuestions.filter(q => q.project_id === pid);
        const projectName = getProjectName(pid) || projectQuestions[0]?.module_reference_name || `Project ${pid}`;
        const stats = calculateDealroomStats(projectQuestions);
        grouped[projectName] = {
          projectId: pid,
          questions: projectQuestions,
          stats: stats
        };
      });

      // Also include projects with no questions (from projects list)
      if (projects && projects.length > 0) {
        projects.forEach(project => {
          const projectName = project.project_name;
          if (!grouped[projectName]) {
            const stats = calculateDealroomStats([]);
            grouped[projectName] = {
              projectId: project.id,
              questions: [],
              stats: stats
            };
          }
        });
      }

      console.log('Grouped questions:', grouped);
      console.log('Number of dealrooms:', Object.keys(grouped).length);
      setGroupedQuestions(grouped);
    } else {
      setGroupedQuestions({});
    }
  }, [isDealQuestions, isSingleDealroom, filteredQuestions, projects, getProjectName, calculateDealroomStats]);

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              {isDealQuestions ? (
                <MessageSquare className="h-8 w-8 text-blue-600" />
              ) : (
                <HelpCircle className="h-8 w-8 text-blue-600" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
              <p className="text-gray-600 mt-1">
                {pageSubtitle}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowQuestionForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{buttonText}</span>
          </button>
        </div>

        {/* Info Banner */}
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900 font-medium">
                Questions submitted here are sent directly to site administrators
              </p>
              <p className="text-xs text-blue-700 mt-1">
                For dataroom-specific questions, please use the Q&A section within the relevant dataroom project.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              placeholder="Search questions..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="answered">Answered</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={filters.category}
              onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Categories</option>
              <option value="General">General</option>
              <option value="Technical">Technical</option>
              <option value="Support">Support</option>
            </select>
          </div>
        </div>
      </div>

      {/* Questions List */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading {isDealQuestions ? 'deal' : 'support'} questions...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        ) : isSingleDealroom && isDealQuestions ? (
          /* Show flat list for single dealroom */
          filteredQuestions.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No questions yet</h3>
              <p className="text-gray-600 mb-4">
                No questions have been submitted for this dealroom yet.
              </p>
              <button
                onClick={() => navigate('/questions/dealrooms')}
                className="px-4 py-2 text-blue-600 hover:text-blue-800"
              >
                ← Back to All Dealrooms
              </button>
            </div>
          ) : (
            <>
              {/* Back button */}
              <div className="mb-4">
                <button
                  onClick={() => navigate('/questions/dealrooms')}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  ← Back to All Dealrooms
                </button>
              </div>
              
              {/* All questions for this dealroom */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="divide-y divide-gray-200">
                  {filteredQuestions.map((question) => (
                    <div
                      key={question.id}
                      className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/questions/detail/${question.id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{question.title}</h3>
                            {getStatusBadge(question.status)}
                            {question.priority === 'high' && (
                              <span className="text-xs px-2 py-1 rounded border bg-red-50 text-red-800 border-red-200">
                                High Priority
                              </span>
                            )}
                          </div>
                          
                          {/* Folder/File Context */}
                          {(question.folder_name || question.file_name) && (
                            <div className="text-sm text-blue-600 mb-2">
                              {question.folder_name && `${question.folder_name}`}
                              {question.file_name && ` > ${question.file_name}`}
                            </div>
                          )}
                          
                          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                            {question.description || 'No description provided'}
                          </p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span>By {question.user_name || 'Unknown'}</span>
                            <span>•</span>
                            <span>Category: {question.category || 'General'}</span>
                            <span>•</span>
                            <span>{formatDate(question.created_at)}</span>
                            {question.responses && question.responses.length > 0 && (
                              <>
                                <span>•</span>
                                <span className="flex items-center space-x-1">
                                  <MessageSquare className="h-3 w-3" />
                                  <span>{question.responses.length} response{question.responses.length !== 1 ? 's' : ''}</span>
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="ml-4">
                          <CheckCircle className="h-5 w-5 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )
        ) : isDealQuestions && Object.keys(groupedQuestions).length > 0 ? (
          /* Show grouped by dealroom for deal questions */
          Object.entries(groupedQuestions)
            .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
            .map(([dealroomName, { projectId: pid, questions: projectQuestions, stats }]) => (
              <div key={pid} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Dealroom Header with Stats - Clickable */}
                <div 
                  className="bg-blue-50 border-b border-blue-200 px-6 py-4 cursor-pointer hover:bg-blue-100 transition-colors"
                  onClick={() => navigate(`/questions/dealroom/${pid}`)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-blue-900">{dealroomName}</h3>
                    <span className="text-sm text-blue-700 font-medium">
                      View All →
                    </span>
                  </div>
                  
                  {/* Statistics */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="flex items-center space-x-2">
                      <TrendingUp className="h-4 w-4 text-blue-600" />
                      <div>
                        <div className="text-xs text-gray-600">Total</div>
                        <div className="text-lg font-bold text-gray-900">{stats.total}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ClockIcon className="h-4 w-4 text-orange-600" />
                      <div>
                        <div className="text-xs text-gray-600">Open</div>
                        <div className="text-lg font-bold text-orange-600">{stats.open}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckIcon className="h-4 w-4 text-green-600" />
                      <div>
                        <div className="text-xs text-gray-600">Answered</div>
                        <div className="text-lg font-bold text-green-600">{stats.answered}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-purple-600" />
                      <div>
                        <div className="text-xs text-gray-600">Unread</div>
                        <div className="text-lg font-bold text-purple-600">{stats.unread}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preview of recent questions (first 3) */}
                {projectQuestions.length === 0 ? (
                  <div className="px-4 py-8 text-center text-gray-500 text-sm">
                    No questions submitted for this dealroom
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {projectQuestions.slice(0, 3).map((question) => (
                      <div
                        key={question.id}
                        className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/questions/detail/${question.id}`)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h3 className="text-base font-semibold text-gray-900">{question.title}</h3>
                              {/* Status */}
                              <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700">
                                {question.status || 'Open'}
                              </span>
                              {/* Priority */}
                              {question.priority === 'high' && (
                                <span className="text-xs px-2 py-1 rounded border bg-red-50 text-red-800 border-red-200">
                                  High Priority
                                </span>
                              )}
                            </div>
                            
                            {/* Folder/File Context */}
                            {(question.folder_name || question.file_name) && (
                              <div className="text-sm text-blue-600 mb-2">
                                {question.folder_name && `${question.folder_name}`}
                                {question.file_name && ` > ${question.file_name}`}
                              </div>
                            )}
                            
                            <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                              {question.description || 'No description provided'}
                            </p>
                            <div className="flex items-center space-x-3 text-xs text-gray-500">
                              <span>{question.user_name || 'Unknown'}</span>
                              <span>•</span>
                              <span>{question.category}</span>
                              <span>•</span>
                              <span>{formatDate(question.created_at)}</span>
                              {question.responses && question.responses.length > 0 && (
                                <>
                                  <span>•</span>
                                  <span className="text-blue-600 font-medium">
                                    {question.responses.length} response{question.responses.length !== 1 ? 's' : ''}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Show "View all" button if there are more than 3 questions */}
                    {projectQuestions.length > 3 && (
                      <div className="p-4 bg-gray-50 border-t border-gray-200 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/questions/dealroom/${pid}`);
                          }}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          View all {projectQuestions.length} questions →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
        ) : filteredQuestions.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No {isDealQuestions ? 'deal' : 'support'} questions yet</h3>
            <p className="text-gray-600 mb-4">
              {filters.search || filters.status !== 'all' || filters.category !== 'all'
                ? 'No questions match your filters. Try adjusting your search criteria.'
                : `You haven't submitted any ${isDealQuestions ? 'deal' : 'support'} questions yet. Click "${buttonText}" to get started.`}
            </p>
            {!filters.search && filters.status === 'all' && filters.category === 'all' && (
              <button
                onClick={() => setShowQuestionForm(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {buttonText}
              </button>
            )}
          </div>
        ) : (
          /* Show flat list for support questions */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="divide-y divide-gray-200">
              {filteredQuestions.map((question) => (
                <div
                  key={question.id}
                  className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/questions/detail/${question.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{question.title}</h3>
                        {getStatusBadge(question.status)}
                      </div>
                      
                      {/* Dealroom Context Path */}
                      {question.module_type === 'dataroom' && (
                        <div className="text-sm text-blue-600 mb-2">
                          {question.module_reference_name || 'Dealroom'}
                          {question.folder_name && ` > ${question.folder_name}`}
                          {question.file_name && ` > ${question.file_name}`}
                        </div>
                      )}
                      
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {question.description || 'No description provided'}
                      </p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>By {question.user_name || 'Unknown'}</span>
                        <span>•</span>
                        <span>Category: {question.category || 'General'}</span>
                        <span>•</span>
                        <span>
                          {formatDate(question.created_at)}
                        </span>
                        {question.responses && question.responses.length > 0 && (
                          <>
                            <span>•</span>
                            <span className="flex items-center space-x-1">
                              <MessageSquare className="h-3 w-3" />
                              <span>{question.responses.length} response{question.responses.length !== 1 ? 's' : ''}</span>
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="ml-4">
                      <CheckCircle className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Question Form Modal */}
      <ContextAwareQuestionForm
        isOpen={showQuestionForm}
        onClose={() => setShowQuestionForm(false)}
        context={{
          moduleType: moduleType,
          moduleReferenceId: null,
          moduleReferenceName: null,
          projectId: null,
          companyId: null
        }}
        onSubmit={handleQuestionCreated}
        user={user}
      />
    </div>
  );
};

export default SupportQuestionsPage;



